"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useUpplasning } from "@/lib/upplasning";
import { tala } from "@/lib/upplasning/tal";
import { taltextFran } from "@/lib/upplasning/text-fran-dom";
import InlineMath from "@/components/exercise/InlineMath";

interface Popover { ord: string; forklaring: string; kontext: string; left: number; top: number; bottom: number; placeAbove: boolean }

// Rough upper-bound estimate of popover height (word + explanation + context
// line + padding). We don't know the real height until after render, so we
// flip to "above" pre-emptively when there isn't clearly enough room below —
// simpler and more robust than a post-render measure-and-reposition pass,
// since the layout is viewport-locked and can never scroll to reveal an
// overflowing popover.
const ESTIMATED_POPOVER_HEIGHT = 180;

// Global tap-to-explain layer for ordstöd spans (data-ordstod). One delegated
// listener — no per-word hydration. Popover shows the word, its plain
// explanation, the context line, and a speak button (Web Speech API, sv-SE).
export default function OrdstodLayer() {
  const [pop, setPop] = useState<Popover | null>(null);
  // Popoverns textblock — uppläsningen läser ur den renderade DOM:en.
  const textRef = useRef<HTMLDivElement | null>(null);
  const { pa, hastighet, rostURI } = useUpplasning();

  // No SSR-mount guard needed: `pop` starts null and is only ever set from a
  // client-side event listener (registered in the effect below), so the
  // portal branch never renders during SSR/hydration.
  useEffect(() => {
    function onPointerDown(e: MouseEvent) {
      const target = e.target as HTMLElement | null;
      if (target?.closest("[data-ordstod-popover]")) return; // clicks inside popover
      const el = target?.closest<HTMLElement>("[data-ordstod]");
      if (!el) { setPop(null); return; }
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const placeAbove = r.bottom + 8 + ESTIMATED_POPOVER_HEIGHT > window.innerHeight;
      setPop({
        ord: el.textContent ?? "",
        forklaring: el.getAttribute("data-ordstod-f") ?? "",
        kontext: el.getAttribute("data-ordstod-k") ?? "",
        left: Math.min(Math.max(r.left + r.width / 2, 150), window.innerWidth - 150),
        top: r.bottom + 8,
        bottom: window.innerHeight - r.top + 8,
        placeAbove,
      });
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setPop(null);
      if ((e.key === "Enter" || e.key === " ") && (e.target as HTMLElement)?.hasAttribute?.("data-ordstod")) {
        (e.target as HTMLElement).dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
        e.preventDefault();
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Reads through the shared playback singleton, so tapping a word interrupts
  // a paragraph that is being read instead of talking over it.
  function speak() {
    if (!pop) return;
    // Läs ur den renderade DOM:en, inte ur råsträngarna: taltextFran översätter
    // KaTeX-noder till svenska ("minus tre upphöjt till två") via samma väg som
    // brödtexten. Läste vi råsträngen skulle eleven höra dollartecknen.
    const brodtext = textRef.current
      ? taltextFran(textRef.current)
      : `${pop.forklaring} ${pop.kontext}`;
    tala("ordstod", `${pop.ord}. ${brodtext}`, { hastighet, rostURI });
  }

  if (!pop) return null;
  return createPortal(
    <div
      data-ordstod-popover=""
      role="dialog"
      aria-label={`Förklaring av ordet ${pop.ord}`}
      style={{
        position: "fixed", left: pop.left, transform: "translateX(-50%)",
        ...(pop.placeAbove ? { bottom: pop.bottom } : { top: pop.top }),
        maxWidth: "min(340px, 88vw)", zIndex: 9999,
        background: "var(--color-bg-card)",
        border: "1px solid color-mix(in oklab, var(--color-text) 15%, transparent)",
        borderRadius: "10px", boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
        padding: "0.875rem 1rem", fontSize: "1rem", lineHeight: 1.7,
        color: "var(--color-text)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem" }}>
        <span style={{ fontWeight: 600 }}>{pop.ord}</span>
        {pa && (
          <button
            type="button"
            onClick={speak}
            title="Läs upp ordet och förklaringen högt"
            style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", fontSize: "1.1rem", color: "var(--color-accent)" }}
          >
            🔊
          </button>
        )}
      </div>
      {/* Rendered through InlineMath, not as plain text: kontext-raderna
          innehåller ofta matematik ("…som när $(-3)^2$ betyder…") och visades
          annars med råa dollartecken. Blocken kan bli <div>, så behållaren är
          <div> och inte <p> — annars bryts den ogiltiga nästlingen upp av
          webbläsaren och styckena hamnar utanför popovern. */}
      <div ref={textRef} style={{ margin: 0 }}>
        <InlineMath source={pop.forklaring} />
        {pop.kontext && (
          <div style={{ margin: "0.375rem 0 0", opacity: 0.85 }}>
            <InlineMath source={pop.kontext} />
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
